import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import LocationPicker, { Location } from './LocationPicker';

interface FavoriteLocation {
  id: string;
  name: string;
  address: string;
  coordinates: { lat: number; lng: number };
  type?: 'restaurant' | 'cafe' | 'office' | 'public' | 'other';
  createdAt: Date | string;
  usageCount?: number;
}

interface FavoriteLocationsManagerProps {
  favoriteLocations: FavoriteLocation[];
  onUpdate: (locations: FavoriteLocation[]) => void;
  maxLocations?: number;
}

const FavoriteLocationsManager: React.FC<FavoriteLocationsManagerProps> = ({
  favoriteLocations,
  onUpdate,
  maxLocations = 2,
}) => {
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [editingLocation, setEditingLocation] = useState<FavoriteLocation | null>(null);

  const handleSaveFavorite = async (location: Location) => {
    if (!auth.currentUser) return;

    // Provide default coordinates if not provided (Amsterdam center as fallback)
    const coordinates = location.coordinates || { lat: 52.3676, lng: 4.9041 };

    const newLocation: FavoriteLocation = {
      id: editingLocation?.id || `fav_${Date.now()}`,
      name: location.name,
      address: location.address,
      coordinates: coordinates,
      type: 'other',
      createdAt: editingLocation?.createdAt || new Date(),
      usageCount: editingLocation?.usageCount || 0,
    };

    const updatedLocations = editingLocation
      ? favoriteLocations.map((loc) => (loc.id === editingLocation.id ? newLocation : loc))
      : [...favoriteLocations, newLocation].slice(0, maxLocations);

    try {
      await updateDoc(doc(db, 'userProfiles', auth.currentUser.uid), {
        favoriteLocations: updatedLocations,
        updatedAt: serverTimestamp(),
      });

      onUpdate(updatedLocations);
      setShowLocationPicker(false);
      setEditingLocation(null);
    } catch (error) {
      console.error('Error saving favorite location:', error);
    }
  };

  const handleDeleteFavorite = async (locationId: string) => {
    if (!auth.currentUser) return;

    const updatedLocations = favoriteLocations.filter((loc) => loc.id !== locationId);

    try {
      await updateDoc(doc(db, 'userProfiles', auth.currentUser.uid), {
        favoriteLocations: updatedLocations,
        updatedAt: serverTimestamp(),
      });

      onUpdate(updatedLocations);
    } catch (error) {
      console.error('Error deleting favorite location:', error);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          ⭐ Favoriete Locaties ({favoriteLocations.length}/{maxLocations})
        </label>
        {favoriteLocations.length < maxLocations && (
          <button
            onClick={() => {
              setEditingLocation(null);
              setShowLocationPicker(true);
            }}
            className="text-xs px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            + Toevoegen
          </button>
        )}
      </div>

      {favoriteLocations.length > 0 && (
        <div className="space-y-2">
          {favoriteLocations.map((fav) => (
            <div
              key={fav.id}
              className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">{fav.name}</div>
                <div className="text-xs text-gray-600 mt-0.5">{fav.address}</div>
                {fav.usageCount !== undefined && fav.usageCount > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    {fav.usageCount}x gebruikt
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setEditingLocation(fav);
                    setShowLocationPicker(true);
                  }}
                  className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700"
                  title="Bewerken"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDeleteFavorite(fav.id)}
                  className="text-xs px-2 py-1 text-red-600 hover:text-red-700"
                  title="Verwijderen"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {favoriteLocations.length === 0 && (
        <div className="text-center py-4 text-sm text-gray-500">
          <p>Nog geen favoriete locaties</p>
          <p className="text-xs mt-1">Voeg er {maxLocations} toe voor sneller plannen</p>
        </div>
      )}

      {showLocationPicker && (
        <div className="mt-4 p-4 bg-white border rounded-lg">
          <LocationPicker
            onLocationSelect={handleSaveFavorite}
            initialLocation={editingLocation || undefined}
            onClose={() => {
              setShowLocationPicker(false);
              setEditingLocation(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default FavoriteLocationsManager;



